import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Button, Label, SearchField } from "@heroui/react";
import { ThemeToggle } from "@branding/ui";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { fetchCart, loginUrl, registerUrl } from "../lib/api";
import type { Cart } from "../lib/types";

const AISLES = [
  { slug: "makanan", name: "Makanan" },
  { slug: "kerajinan", name: "Kerajinan" },
  { slug: "fashion", name: "Fashion" },
  { slug: "pertanian", name: "Pertanian" },
] as const;

export function StorefrontChrome({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [mounted, setMounted] = useState(false);
  const cart = useSWR<Cart>(mounted ? "cart" : null, fetchCart);

  useEffect(() => {
    setMounted(true);
  }, []);

  const count = cart.data?.lines.reduce((sum, line) => sum + line.qty, 0) ?? 0;

  function onSearch(event: FormEvent) {
    event.preventDefault();
    const q = query.trim();
    if (!q) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-3 px-4 py-3 md:px-6">
          <a href="/pasar" className="text-lg font-semibold tracking-tight no-underline">
            Pasar Kupang
          </a>
          <nav className="hidden items-center gap-4 text-sm md:flex" aria-label="Lorong pasar">
            {AISLES.map((aisle) => (
              <a
                key={aisle.slug}
                href={`/pasar/kategori/${aisle.slug}`}
                className="text-muted no-underline hover:text-foreground"
              >
                {aisle.name}
              </a>
            ))}
          </nav>
          <form onSubmit={onSearch} className="min-w-48 flex-1">
            <SearchField
              value={query}
              onChange={setQuery}
              className="w-full"
              name="q"
            >
              <Label className="sr-only">Cari produk</Label>
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder="Cari tenun, sei, kopi…" />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          </form>
          <div className="flex items-center gap-2">
            <Button variant="tertiary" onPress={() => navigate("/cart")}>
              Keranjang{mounted && count > 0 ? ` (${count})` : ""}
            </Button>
            <Button variant="secondary" onPress={() => window.location.assign(loginUrl("/pasar"))}>
              Masuk
            </Button>
            <Button variant="primary" onPress={() => window.location.assign(registerUrl("/pasar"))}>
              Daftar
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="mt-16 border-t border-border">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap gap-x-6 gap-y-2 px-4 py-8 text-sm text-muted md:px-6">
          <p>Pasar Kupang · UMKM Kota Kupang</p>
          <a href="/syarat">Syarat</a>
          <a href="/privasi">Privasi</a>
          <a href="/pengembalian">Pengembalian</a>
        </div>
      </footer>
    </div>
  );
}
